import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

const FILTROS = [
  { key: 'activas',    label: 'Activas',        color: '#16a34a' },
  { key: 'por_vencer', label: 'Próximas a vencer', color: '#f59e0b' },
  { key: 'vencidas',   label: 'Vencidas',       color: '#ef4444' },
  { key: 'canceladas', label: 'Canceladas',     color: '#64748b' },
];

const fmtDinero = v => '$ ' + Number(v || 0).toLocaleString('es-CO');
const fmtNum    = v => Number(v || 0).toLocaleString('es-CO');

const fmtFecha = f => {
  if (!f) return '—';
  const [y, m, d] = String(f).split('-');
  return d ? `${d}/${m}/${y}` : String(f);
};

export default function SubscriptionsPage() {
  const [resumen, setResumen]   = useState(null);
  const [lista, setLista]       = useState([]);
  const [filtro, setFiltro]     = useState('activas');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [ok, setOk]             = useState('');
  const [error, setError]       = useState('');
  const [renovando, setRenovando] = useState(null);
  const esMovil = useIsMobile();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [r, l] = await Promise.all([
        api.get('/subscriptions/summary'),
        api.get('/subscriptions', { params: { filtro, search: busqueda || undefined } }),
      ]);
      setResumen(r.data);
      setLista(l.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cargando suscripciones');
      setTimeout(() => setError(''), 5000);
    } finally { setCargando(false); }
  }, [filtro, busqueda]);

  useEffect(() => { cargar(); }, [cargar]);

  const renovar = async (s) => {
    const meses = window.prompt(`¿Cuántos meses renovar la suscripción de "${s.negocio}"?`, '1');
    if (!meses) return;
    setRenovando(s.id);
    try {
      const r = await api.patch(`/subscriptions/${s.id}/renovar`, { meses: Number(meses) });
      setOk(`${s.negocio}: ${r.data.message}. Nuevo vencimiento ${fmtFecha(r.data.vence)}`);
      setTimeout(() => setOk(''), 6000);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo renovar');
      setTimeout(() => setError(''), 5000);
    } finally { setRenovando(null); }
  };

  const tarjeta = { background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '1.1rem 1.25rem' };

  const kpis = [
    { label: 'Suscripciones activas', valor: fmtNum(resumen?.activas),          icono: '👥', color: '#8b5cf6' },
    { label: 'Ingreso mensual (MRR)', valor: fmtDinero(resumen?.mrr),           icono: '💵', color: '#16a34a' },
    { label: 'Ingreso anual (ARR)',   valor: fmtDinero(resumen?.arr),           icono: '📈', color: '#f59e0b' },
    { label: 'Ticket promedio',       valor: fmtDinero(resumen?.ticket_promedio), icono: '🎫', color: '#2563eb' },
  ];

  // Color del contador de días restantes según la urgencia
  const colorDias = d => {
    if (d === null || d === undefined) return 'var(--color-text-muted)';
    if (d < 0)  return '#ef4444';
    if (d <= 7) return '#f59e0b';
    return '#16a34a';
  };

  const textoDias = d => {
    if (d === null || d === undefined) return 'Sin fecha';
    if (d < 0)  return `Vencida hace ${Math.abs(d)} d`;
    if (d === 0) return 'Vence hoy';
    return `En ${d} días`;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-text">
          <h2>Suscripciones</h2>
          <p>Gestiona las suscripciones de todos los negocios de la plataforma</p>
        </div>
      </div>

      {ok    && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{ok}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* KPIs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem', marginBottom: '1.25rem',
      }}>
        {kpis.map(k => (
          <div key={k.label} style={tarjeta}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: k.color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{k.icono}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', lineHeight: 1.2 }}>{k.label}</div>
            </div>
            <div style={{ fontSize: esMovil ? '1.15rem' : '1.45rem', fontWeight: 800 }}>{k.valor}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1fr 300px', gap: '1.25rem', alignItems: 'start' }}>
        {/* Lista */}
        <div style={{ ...tarjeta, minWidth: 0 }}>
          {/* Filtros + búsqueda */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {FILTROS.map(f => {
              const activo = filtro === f.key;
              const conteo = resumen?.[f.key];
              return (
                <button
                  key={f.key}
                  onClick={() => setFiltro(f.key)}
                  style={{
                    padding: '7px 13px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem',
                    fontWeight: activo ? 700 : 500,
                    border: `1px solid ${activo ? f.color : 'var(--color-border)'}`,
                    background: activo ? f.color + '18' : 'transparent',
                    color: activo ? f.color : 'var(--color-text-muted)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {f.label}{conteo !== undefined ? ` (${conteo})` : ''}
                </button>
              );
            })}
          </div>

          <input
            className="form-input"
            placeholder="Buscar negocio o ciudad..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ marginBottom: 14 }}
          />

          {cargando ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : lista.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
              No hay suscripciones en este filtro
            </div>
          ) : esMovil ? (
            // En celular se apila cada suscripción como tarjeta
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {lista.map(s => (
                <div key={s.id} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: '0.85rem' }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{s.negocio}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    {s.propietario || 'Sin propietario'} · {s.ciudad || 'Sin ciudad'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 4 }}>
                    <span>{s.plan || 'Sin plan'}</span>
                    <strong>{fmtDinero(s.monto)}/mes</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Vence {fmtFecha(s.vence)}</span>
                    <span style={{ color: colorDias(s.dias_restantes), fontWeight: 600 }}>{textoDias(s.dias_restantes)}</span>
                  </div>
                  <button className="btn btn-secondary" style={{ width: '100%', marginTop: 10, fontSize: '0.82rem' }}
                    disabled={renovando === s.id} onClick={() => renovar(s)}>
                    {renovando === s.id ? 'Renovando...' : '🔄 Renovar'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Negocio</th><th>Propietario</th><th>Plan</th><th>Monto</th>
                    <th>Vence</th><th>Estado</th><th>Usuarios</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.negocio}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{s.ciudad || '—'}</div>
                      </td>
                      <td style={{ fontSize: '0.83rem' }}>
                        {s.propietario || '—'}
                        <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)' }}>{s.propietario_email || ''}</div>
                      </td>
                      <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{s.plan || 'Sin plan'}</span></td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtDinero(s.monto)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '0.83rem' }}>{fmtFecha(s.vence)}</div>
                        <div style={{ fontSize: '0.73rem', color: colorDias(s.dias_restantes), fontWeight: 600 }}>
                          {textoDias(s.dias_restantes)}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.78rem', color: s.estado === 'active' ? '#16a34a' : '#ef4444' }}>
                          {s.estado === 'active' ? 'Activa' : s.estado === 'suspended' ? 'Suspendida' : s.estado}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.83rem' }}>
                        {s.usuarios}{s.max_usuarios ? ` / ${s.max_usuarios}` : ''}
                      </td>
                      <td>
                        <button className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                          disabled={renovando === s.id} onClick={() => renovar(s)}>
                          {renovando === s.id ? '...' : '🔄 Renovar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Ingresos por plan */}
        <div style={{ ...tarjeta, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 14 }}>Ingresos por plan (MRR)</div>
          {(resumen?.por_plan || []).length === 0 ? (
            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.87rem' }}>Sin datos</div>
          ) : (
            <>
              {resumen.por_plan.map((p, i) => {
                const pct = resumen.mrr ? Math.round(Number(p.mrr) * 100 / resumen.mrr) : 0;
                const color = ['#8b5cf6', '#2563eb', '#16a34a', '#f59e0b'][i % 4];
                return (
                  <div key={p.plan} style={{ marginBottom: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 4 }}>
                      <span>{p.plan}</span>
                      <strong>{fmtDinero(p.mrr)}</strong>
                    </div>
                    <div style={{ height: 6, borderRadius: 99, background: 'var(--color-surface)' }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: color }} />
                    </div>
                    <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
                      {p.suscripciones} negocio(s) · {pct}%
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 10, marginTop: 4, fontSize: '0.9rem' }}>
                <strong>Total</strong>
                <strong style={{ color: 'var(--color-gold)' }}>{fmtDinero(resumen.mrr)}</strong>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
